import { STOCK_ADJUSTMENT_REASONS } from '@api/cm-sites';
import type { SiteLedgerEntry } from '@api/vendor-portal';
import { REJECTION_REASON_OPTIONS } from '@components/containers/vendorReceive/rows';

/**
 * A line in the history.
 *
 * A declaration is ONE line with the movements nested under it, because that is how it happened
 * and how it undoes. Showing a run as five sibling rows — finished goods here, four materials
 * scattered among the day's other entries — describes the database rather than the day, and it
 * invites somebody to undo the finished goods and leave the material consumed.
 */
export interface LedgerRow {
    key: string;
    /** A production declaration or a delivery (one GRN), or a single movement. */
    kind: 'bundle' | 'event';
    date: string;
    recordedAt: string;
    label: string;
    itemId: string;
    /** What a person calls it, resolved by the server. Falls back to the id. */
    itemName: string;
    batchCode: string | null;
    batchId?: string | null;
    /** The site received it without naming a supplier; the brand still has to. */
    awaitingSupplier?: boolean;
    /** Signed for display: what this did to the balance. Bundles show their net. */
    quantity: number;
    reference: string | null;
    enteredByName: string | null;
    provenance: 'CM' | 'BRAND' | 'OPENING' | 'SYNC';
    /** Per-unit price paid, on receipts. Null until the brand records it. */
    unitCost: number | null;
    currency: string | null;
    /** Whether this row is a receipt, i.e. the only kind that can carry a cost. */
    isReceipt: boolean;
    /** Set on rows that undo something, and on rows that have been undone. */
    reversalOfId: string | null;
    reversedByEventId: string | null;
    /** The event the undo button acts on. A bundle undoes through any of its members. */
    reversibleEventId: string | null;
    /**
     * A movement shown inside a declaration. It carries no undo of its own: undoing reaches
     * the whole run, and a button on one line of it would promise something narrower than what
     * would actually happen.
     */
    isChild?: boolean;
    notes: string | null;
    reasonCode: string | null;
    /** The goods receipt a delivery's rows share. */
    grnId: string | null;
    purchaseOrderId: string | null;
    children?: LedgerRow[];
}

export const MOVEMENT_LABEL: Record<string, string> = {
    RM_INWARD: 'Received',
    RM_CONSUMPTION: 'Used in production',
    RM_RETURN: 'Returned to supplier',
    FG_PRODUCTION: 'Produced',
    FG_DISPATCH: 'Dispatched',
    WASTAGE: 'Wasted',
    ADJUSTMENT: 'Adjusted',
    OPENING_BALANCE: 'Opening balance',
    MARKED_BAD: 'Marked bad',
};

export function movementLabel(entry: SiteLedgerEntry): string {
    if (!entry.movementType) return entry.incoming ? 'Stock in' : 'Stock out';
    return MOVEMENT_LABEL[entry.movementType] ?? entry.movementType;
}

// The words the person picked, not the code the server stored. Anything not listed here (the
// wastage reasons, say) reads well enough with its underscores taken out.
const REASON_LABEL: Record<string, string> = {
    ...Object.fromEntries(STOCK_ADJUSTMENT_REASONS.map((reason) => [reason.value, reason.label])),
    ...Object.fromEntries(
        REJECTION_REASON_OPTIONS.flatMap((group) => group.items).map((item) => [item.value, item.label]),
    ),
    FEFO_DEVIATION: 'Not the suggested batch',
};

/** Why, and the note if one was typed: "Damage found on the shelf · two rolls torn". */
export function reasonText(row: Pick<LedgerRow, 'reasonCode' | 'notes'>): string | null {
    const reason = row.reasonCode
        ? REASON_LABEL[row.reasonCode] ??
          row.reasonCode.charAt(0) + row.reasonCode.slice(1).toLowerCase().replace(/_/g, ' ')
        : null;
    const parts = [reason, row.notes?.trim() || null].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
}

/**
 * Where a row came from, as the brand needs to read it.
 *
 * A person at the site, the brand acting on their behalf, and a seeded opening balance are
 * three different kinds of claim about the world, and a ledger that showed them identically
 * would let the weakest of them borrow the authority of the strongest.
 */
export function provenanceOf(entry: SiteLedgerEntry): LedgerRow['provenance'] {
    switch (entry.entrySource) {
        case 'CM_PORTAL':
            return 'CM';
        case 'BRAND_ON_BEHALF':
            return 'BRAND';
        case 'OPENING_CSV':
            return 'OPENING';
        default:
            // No source at all means no person: it arrived from a connected system.
            return 'SYNC';
    }
}

function toRow(entry: SiteLedgerEntry): LedgerRow {
    return {
        key: entry.id,
        kind: 'event',
        date: entry.date,
        recordedAt: entry.recordedAt,
        label: movementLabel(entry),
        itemId: entry.itemId,
        itemName: entry.itemName ?? entry.itemId,
        batchCode: entry.batchCode,
        batchId: entry.batchId,
        awaitingSupplier: entry.batchOriginType === 'SITE_RECEIPT',
        // Signed, because "40" against a consumption and a receipt are opposite facts and a
        // reader scanning a column should not have to consult the type to tell which.
        quantity: entry.incoming ? entry.quantity : -entry.quantity,
        reference: entry.reference,
        enteredByName: entry.enteredByName,
        provenance: provenanceOf(entry),
        unitCost: entry.unitCost ?? null,
        currency: entry.currency ?? null,
        // Only a receipt carries a price. A reversal of one does too, but it is not something
        // anyone should be costing, so it is excluded from the affordance.
        isReceipt: entry.movementType === 'RM_INWARD' && !entry.reversalOfId,
        reversalOfId: entry.reversalOfId,
        reversedByEventId: entry.reversedByEventId,
        reversibleEventId: entry.reversalOfId ? null : entry.id,
        notes: entry.notes,
        reasonCode: entry.reasonCode,
        grnId: entry.grnId ?? null,
        purchaseOrderId: entry.purchaseOrderId,
    };
}

/**
 * Which one act a movement belongs to, when it belongs to one.
 *
 * A production declaration by its entry id; a delivery by its GRN - the received lines and
 * whatever was turned away at the gate. A delivery's undo carries the same GRN and is kept as
 * its own group, so the original and its correction never fold into one line that nets to zero.
 * A connected system's GRN is the warehouse's, not a site delivery, and stays row by row.
 */
function groupKeyOf(entry: SiteLedgerEntry): string | null {
    if (entry.productionEntryId) return `bundle-${entry.productionEntryId}`;
    if (entry.grnId && entry.entrySource) {
        return `grn-${entry.grnId}${entry.reversalOfId ? '-undo' : ''}`;
    }
    return null;
}

/**
 * Turn a page of movements into what the history shows.
 *
 * UNDONE ENTRIES DISAPPEAR BY DEFAULT, in both directions — the original and the correction
 * that cancelled it. The ledger stores a reversal because a record that can be erased is not a
 * record; the SCREEN presents it as a deletion, because from the manufacturer's side that is
 * what happened and a history where every mistyped entry survives twice is one nobody reads.
 * `showUndone` puts both halves back, which is what somebody asking "what did I undo" needs.
 *
 * Bundling happens after that, so a declaration undone whole vanishes whole rather than leaving
 * an empty heading behind.
 */
export function buildLedgerRows(
    entries: SiteLedgerEntry[],
    options: { showUndone?: boolean } = {},
): LedgerRow[] {
    const visible = options.showUndone
        ? entries
        : entries.filter((entry) => !entry.reversalOfId && !entry.reversedByEventId);

    const bundles = new Map<string, SiteLedgerEntry[]>();
    const rows: LedgerRow[] = [];

    for (const entry of visible) {
        const groupKey = groupKeyOf(entry);
        if (!groupKey) {
            rows.push(toRow(entry));
            continue;
        }
        const existing = bundles.get(groupKey);
        if (existing) existing.push(entry);
        else {
            bundles.set(groupKey, [entry]);
            // A placeholder holds the bundle's position, so the day still reads in order.
            rows.push({ ...toRow(entry), key: groupKey });
        }
    }

    return rows.map((row) => {
        const members = bundles.get(row.key);
        if (!members) return row;

        if (row.key.startsWith('grn-')) return deliveryRow(row.key, members);

        const outputs = members.filter((entry) => entry.movementType === 'FG_PRODUCTION');
        const first = outputs[0] ?? members[0];

        return {
            ...toRow(first),
            key: row.key,
            kind: 'bundle',
            label:
                outputs.length > 1
                    ? `Produced ${outputs.length} SKUs`
                    : movementLabel(first),
            // The item column names the run, not one of its lines: a multi-SKU run has no
            // single item and picking one would quietly hide the others.
            itemId: outputs.length > 1 ? outputs.map((o) => o.itemId).join(', ') : first.itemId,
            itemName:
                outputs.length > 1
                    ? outputs.map((o) => o.itemName ?? o.itemId).join(', ')
                    : first.itemName ?? first.itemId,
            quantity: outputs.reduce((sum, entry) => sum + entry.quantity, 0),
            // Undoing reaches the whole declaration through any member, so the finished goods
            // cannot stay on the shelf while the material that made them comes back.
            reversibleEventId: first.reversalOfId ? null : first.id,
            children: members.map((member) => ({ ...toRow(member), isChild: true })),
        };
    });
}

/**
 * A delivery as one line: what it added to the site, with its received and refused lines under
 * it. A delivery of one clean line needs no nesting and stays a plain row.
 */
function deliveryRow(key: string, members: SiteLedgerEntry[]): LedgerRow {
    if (members.length === 1) return toRow(members[0]);

    const first = members.find((entry) => entry.incoming === !entry.reversalOfId) ?? members[0];
    const items = [...new Set(members.map((entry) => entry.itemId))];
    const names = [...new Set(members.map((entry) => entry.itemName ?? entry.itemId))];

    return {
        ...toRow(first),
        key,
        kind: 'bundle',
        label: first.reversalOfId ? 'Delivery undone' : 'Received',
        itemId: items.join(', '),
        itemName: names.length > 1 ? `${names.length} items` : names[0],
        // Net, signed: what the delivery left on the shelf - 100 in, 2 refused, is +98.
        quantity: members.reduce(
            (sum, entry) => sum + (entry.incoming ? entry.quantity : -entry.quantity),
            0,
        ),
        // A cost belongs to a received line, not to the delivery; it is shown on the children.
        isReceipt: false,
        unitCost: null,
        // Undoing reaches the whole delivery through any of its rows.
        reversibleEventId: first.reversalOfId ? null : first.id,
        children: members.map((member) => ({ ...toRow(member), isChild: true })),
    };
}

/**
 * Whether an entry is still inside the window in which a site may undo its own record.
 *
 * Measured from when it was RECORDED, not from the date it claims — backdating a movement must
 * not backdate the chance to erase it.
 */
export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isUndoable(row: LedgerRow, now: number = Date.now()): boolean {
    if (row.isChild) return false;
    if (!row.reversibleEventId || row.reversedByEventId) return false;
    if (row.provenance === 'SYNC') return false;

    const recorded = new Date(row.recordedAt).getTime();
    return Number.isFinite(recorded) && now - recorded < UNDO_WINDOW_MS;
}
